import React, { useMemo, useRef, useState } from 'react';
import toast from '../../../../utils/toast';
import {
  createOccupancy,
  moveOutOccupancy,
} from '../../../../api/hierarchy-client';
import {
  CreateOccupancyInput,
  IsoDate,
  OCCUPANCY_RELATIONSHIPS,
  OccupancyRelationship,
} from '../../../../interfaces/hierarchy-interfaces';
import {
  IndicatorGroup,
  InlineMessage,
  StatusLabel,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
import {
  Banner,
  CheckRow,
  CheckText,
  Field,
  FieldError,
  Form,
  FormRow,
  GhostButton,
  Hint,
  Input,
  Label,
  Modal,
  ModalActions,
  ModalHeader,
  ModalSub,
  ModalTitle,
  PrimaryButton,
  Scrim,
  Segmented,
  SegmentedIndicator,
  SegmentedLabel,
  SegmentedTab,
  Select,
  Textarea,
} from './dashboard-units.styled';
import {
  occupantFullName,
  readError,
  relationshipLabel,
  splitName,
  todayIso,
  useOrgUsers,
} from './units-shared';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import {
  FUTURE_MOVE_OUT,
  withPassesSentence,
} from '../../../../utils/move-out-passes';
import { ModalPortal } from '../../../modal/modal-portal';

/**
 * Move-in and move-out — the two writes that make the occupancy model real.
 *
 * Neither one destroys anything: a move-in appends a dated row, a move-out
 * stamps moved_out on it. "Who lived in 4B last March" stays answerable
 * forever, which is the entire point of storing occupancy as history rather
 * than as a resident column on the unit.
 */

/* ───────────────────────────  Move in  ──────────────────────────── */

// An occupant either HAS a login or does not. That is a genuine product
// capability — a spouse, a minor, a short-term guest is still someone the front
// desk must be able to look up — so the form asks the question outright instead
// of hiding "no account" behind a blank user picker.
type OccupantMode = 'existing' | 'name';

interface MoveInProps {
  unitId: number;
  unitNumber: string;
  // Name of the current open primary occupant, if any. Drives the displacement
  // warning: claiming primary while someone holds it demotes them.
  currentPrimaryName: string | null;
  // False for roles that cannot read the org directory (a concierge holds no
  // user:read), which turns off the account picker rather than 403-ing.
  canPickUser: boolean;
  // Today's date in the BUILDING's zone (GET /units/:id `building_today`). The
  // default move-in date: a desk in New York at 20:30 is still on today.
  today?: IsoDate;
  onClose: () => void;
  onSaved: () => void;
}

export const MoveInForm: React.FC<MoveInProps> = ({
  unitId,
  unitNumber,
  currentPrimaryName,
  canPickUser,
  today,
  onClose,
  onSaved,
}) => {
  const {
    users,
    loading: usersLoading,
    unavailable,
  } = useOrgUsers(canPickUser);
  const [mode, setMode] = useState<OccupantMode>(
    canPickUser ? 'existing' : 'name'
  );
  const [userId, setUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [relationship, setRelationship] =
    useState<OccupancyRelationship>('resident');
  // A unit with nobody holding the primary slot should get one by default —
  // that is what a first move-in almost always means.
  const [isPrimary, setIsPrimary] = useState(currentPrimaryName === null);
  const [movedIn, setMovedIn] = useState(today || todayIso());
  const [leaseStart, setLeaseStart] = useState('');
  const [leaseEnd, setLeaseEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const saveRef = useRef(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const moveInDialogRef = useDialogFocus<HTMLDivElement>(true, () => {
    if (!saveRef.current) {
      onClose();
    }
  });
  const modalMotion = useOverlayMotion('modal');

  // The picker lost its data source mid-flight — fall back rather than stall.
  const pickerUsable = canPickUser && !unavailable;
  const effectiveMode: OccupantMode = pickerUsable ? mode : 'name';

  const selectedUser = useMemo(
    () => users.find(user => user.id === userId) ?? null,
    [users, userId]
  );

  const displacesPrimary =
    isPrimary && currentPrimaryName !== null && currentPrimaryName.length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (saveRef.current) {
      return;
    }

    setError('');
    setFieldError('');

    const payload: CreateOccupancyInput = {
      relationship,
      is_primary: isPrimary,
      moved_in: movedIn,
      lease_start: leaseStart || null,
      lease_end: leaseEnd || null,
    };

    if (effectiveMode === 'existing') {
      if (!selectedUser) {
        setFieldError('Choose the person moving in, or switch to a name.');

        return;
      }

      // Send the split name alongside user_id: occupancies stores plaintext
      // first/last that the front desk searches, and relying on the server to
      // backfill them from an encrypted profile risks a blank roster entry.
      const parts = splitName(selectedUser.name || selectedUser.email);

      payload.user_id = selectedUser.id;

      if (parts.first_name) {
        payload.first_name = parts.first_name;
        payload.last_name = parts.last_name;
      }
    } else {
      if (!firstName.trim() || !lastName.trim()) {
        setFieldError('A first and last name are both required.');

        return;
      }

      payload.first_name = firstName.trim();
      payload.last_name = lastName.trim();
      // Explicitly no account. The row is a real occupant with no login.
      payload.user_id = null;
    }

    if (!movedIn) {
      setFieldError('A move-in date is required.');

      return;
    }

    if (leaseStart && leaseEnd && leaseEnd < leaseStart) {
      setFieldError('The lease end date cannot be before the lease start.');

      return;
    }

    saveRef.current = true;
    setSaving(true);
    try {
      await createOccupancy(unitId, payload);
      toast.success(`Moved in to unit ${unitNumber}`);
      onSaved();
    } catch (err) {
      setError(readError(err, 'Could not record this move-in.'));
    } finally {
      saveRef.current = false;
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <Scrim
        {...scrimMotion}
        onMouseDown={event => {
          if (event.target === event.currentTarget && !saveRef.current) {
            onClose();
          }
        }}
      >
        <Modal
          {...modalMotion}
          ref={moveInDialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Move in"
          onMouseDown={event => event.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>Move someone in to {unitNumber}</ModalTitle>
            <ModalSub>
              This adds a dated occupancy record. Nothing is overwritten — the
              unit keeps its full history.
            </ModalSub>
          </ModalHeader>

          <Form onSubmit={submit} noValidate>
            {pickerUsable && (
              <Field>
                <Label as="span">Who is moving in?</Label>
                <Segmented role="tablist">
                  {/* One selected pill, sliding between the two tabs. */}
                  <IndicatorGroup id="occupant-mode">
                    <SegmentedTab
                      type="button"
                      role="tab"
                      aria-selected={mode === 'existing'}
                      active={mode === 'existing'}
                      onClick={() => setMode('existing')}
                    >
                      {mode === 'existing' && (
                        <SegmentedIndicator id="occupant-mode" />
                      )}
                      <SegmentedLabel>Someone with an account</SegmentedLabel>
                    </SegmentedTab>
                    <SegmentedTab
                      type="button"
                      role="tab"
                      aria-selected={mode === 'name'}
                      active={mode === 'name'}
                      onClick={() => setMode('name')}
                    >
                      {mode === 'name' && (
                        <SegmentedIndicator id="occupant-mode" />
                      )}
                      <SegmentedLabel>Name only — no login</SegmentedLabel>
                    </SegmentedTab>
                  </IndicatorGroup>
                </Segmented>
                <Hint>
                  {mode === 'existing'
                    ? 'Links the occupancy to their Lobbero account so they can sign in to the resident portal.'
                    : 'For a spouse, a child or a short-term occupant who needs no login but must still be findable at the front desk.'}
                </Hint>
              </Field>
            )}

            {unavailable && (
              <Banner tone="neutral">
                You do not have access to the account directory, so this
                occupant will be recorded by name only. They can be linked to an
                account later.
              </Banner>
            )}

            {effectiveMode === 'existing' ? (
              <Field>
                <Label htmlFor="occupant-user">Account</Label>
                <Select
                  id="occupant-user"
                  value={userId}
                  disabled={usersLoading}
                  onChange={event => setUserId(event.target.value)}
                >
                  <option value="">
                    {usersLoading ? 'Loading people…' : 'Select a person'}
                  </option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name ? `${user.name} — ${user.email}` : user.email}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <FormRow>
                <Field>
                  <Label htmlFor="first-name">First name</Label>
                  <Input
                    id="first-name"
                    value={firstName}
                    onChange={event => setFirstName(event.target.value)}
                    placeholder="Ada"
                  />
                </Field>
                <Field>
                  <Label htmlFor="last-name">Last name</Label>
                  <Input
                    id="last-name"
                    value={lastName}
                    onChange={event => setLastName(event.target.value)}
                    placeholder="Lovelace"
                  />
                </Field>
              </FormRow>
            )}

            <FormRow>
              <Field>
                <Label htmlFor="relationship">Relationship</Label>
                <Select
                  id="relationship"
                  value={relationship}
                  onChange={event =>
                    setRelationship(event.target.value as OccupancyRelationship)
                  }
                >
                  {OCCUPANCY_RELATIONSHIPS.map(value => (
                    <option key={value} value={value}>
                      {relationshipLabel(value)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label htmlFor="moved-in">Move-in date</Label>
                <Input
                  id="moved-in"
                  type="date"
                  value={movedIn}
                  onChange={event => setMovedIn(event.target.value)}
                />
              </Field>
            </FormRow>

            <FormRow>
              <Field>
                <Label htmlFor="lease-start">Lease start (optional)</Label>
                <Input
                  id="lease-start"
                  type="date"
                  value={leaseStart}
                  onChange={event => setLeaseStart(event.target.value)}
                />
              </Field>
              <Field>
                <Label htmlFor="lease-end">Lease end (optional)</Label>
                <Input
                  id="lease-end"
                  type="date"
                  value={leaseEnd}
                  onChange={event => setLeaseEnd(event.target.value)}
                />
              </Field>
            </FormRow>

            <CheckRow htmlFor="is-primary">
              <input
                id="is-primary"
                type="checkbox"
                checked={isPrimary}
                onChange={event => setIsPrimary(event.target.checked)}
              />
              <CheckText>
                Primary occupant
                <Hint>
                  The lease holder and the default contact for this unit. Only
                  one at a time.
                </Hint>
              </CheckText>
            </CheckRow>

            {displacesPrimary && (
              <Banner tone="warning">
                {currentPrimaryName} is currently the primary occupant. Saving
                this will move that status to the new occupant —{' '}
                {currentPrimaryName} stays in the unit.
              </Banner>
            )}

            <FieldError message={fieldError} />
            <InlineMessage
              message={error ? <Banner tone="alert">{error}</Banner> : undefined}
            />

            <ModalActions>
              <GhostButton type="button" onClick={onClose} disabled={saving}>
                Cancel
              </GhostButton>
              <PrimaryButton type="submit" disabled={saving} aria-busy={saving}>
                <StatusLabel
                  state={saving ? 'busy' : 'idle'}
                  labels={{ idle: 'Record move-in', busy: 'Saving…' }}
                />
              </PrimaryButton>
            </ModalActions>
          </Form>
        </Modal>
      </Scrim>
    </ModalPortal>
  );
};

/* ──────────────────────────  Move out  ──────────────────────────── */

// Everything the dialog actually needs, and no more. Both `Occupancy` (from the
// history) and `UnitOccupant` (from GET /units/:id, which omits moved_out and
// is_current) satisfy this structurally, so the same dialog serves the current-
// occupant list and the timeline without either shape being widened to fit.
export interface MoveOutTarget {
  id: string;
  first_name: string;
  last_name: string;
  moved_in: IsoDate;
}

interface MoveOutProps {
  occupancy: MoveOutTarget;
  // Today's date in the BUILDING's zone (GET /units/:id `building_today`). A
  // move-out is today or earlier there — the server refuses a later date, and
  // the date field cannot pick one.
  today?: IsoDate;
  onClose: () => void;
  onSaved: () => void;
}

export const MoveOutForm: React.FC<MoveOutProps> = ({
  occupancy,
  today,
  onClose,
  onSaved,
}) => {
  const lastDay = today || todayIso();
  const [movedOut, setMovedOut] = useState(lastDay);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const saveRef = useRef(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const moveOutDialogRef = useDialogFocus<HTMLDivElement>(true, () => {
    if (!saveRef.current) {
      onClose();
    }
  });
  const modalMotion = useOverlayMotion('modal');

  const name = occupantFullName(occupancy);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (saveRef.current) {
      return;
    }

    setError('');
    setFieldError('');

    if (!movedOut) {
      setFieldError('A move-out date is required.');

      return;
    }

    // A tenancy cannot end before it began; the server rejects this too, but
    // catching it here keeps an obvious typo out of a round trip.
    if (movedOut < occupancy.moved_in) {
      setFieldError('The move-out date cannot be before the move-in date.');

      return;
    }

    // A typed date bypasses the picker's `max`. The server's own sentence.
    if (movedOut > lastDay) {
      setFieldError(FUTURE_MOVE_OUT);

      return;
    }

    saveRef.current = true;
    setSaving(true);
    try {
      const closed = await moveOutOccupancy(occupancy.id, {
        moved_out: movedOut,
        move_out_reason: reason.trim() || null,
      });
      // What happened to the visitor passes they hosted, when there were any:
      // "… 2 visitor passes moved to Ana." / "… 2 visitor passes ended."
      toast.success(withPassesSentence(`${name} moved out`, closed.passes));
      onSaved();
    } catch (err) {
      setError(readError(err, 'Could not record this move-out.'));
    } finally {
      saveRef.current = false;
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <Scrim
        {...scrimMotion}
        onMouseDown={event => {
          if (event.target === event.currentTarget && !saveRef.current) {
            onClose();
          }
        }}
      >
        <Modal
          {...modalMotion}
          ref={moveOutDialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Move out"
          onMouseDown={event => event.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>Move {name} out</ModalTitle>
            <ModalSub>
              The record is closed, not deleted — {name} stays in this
              unit&apos;s history.
            </ModalSub>
          </ModalHeader>

          <Form onSubmit={submit} noValidate>
            <Field>
              <Label htmlFor="moved-out">Move-out date</Label>
              <Input
                id="moved-out"
                type="date"
                value={movedOut}
                min={occupancy.moved_in}
                max={lastDay}
                onChange={event => setMovedOut(event.target.value)}
              />
            </Field>

            <Field>
              <Label htmlFor="move-out-reason">Reason (optional)</Label>
              <Textarea
                id="move-out-reason"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder="Lease ended, transferred to another unit, evicted…"
              />
            </Field>

            <FieldError message={fieldError} />
            <InlineMessage
              message={error ? <Banner tone="alert">{error}</Banner> : undefined}
            />

            <ModalActions>
              <GhostButton type="button" onClick={onClose} disabled={saving}>
                Cancel
              </GhostButton>
              <PrimaryButton type="submit" disabled={saving} aria-busy={saving}>
                <StatusLabel
                  state={saving ? 'busy' : 'idle'}
                  labels={{ idle: 'Record move-out', busy: 'Saving…' }}
                />
              </PrimaryButton>
            </ModalActions>
          </Form>
        </Modal>
      </Scrim>
    </ModalPortal>
  );
};
